import axios from "axios";
import keycloakInstance from "@/initKeycloak.js";
import {
  CalendarV2Event,
  isCalendarV2EventVisible,
} from "@/shared/models/economic-calendar-v2/CalendarV2Event";

const BASE_URL = import.meta.env.VITE_CALENDAR_V2_BASE_URL;

export const client = axios.create({
  baseURL: BASE_URL,
});

// The new backend authenticates with our Keycloak token (realm `Newsquawk`,
// same as the portal). Attach a fresh Bearer on every request — mirrors the
// firehose client (src/shared/api/firehose/index.ts).
client.interceptors.request.use(async (config) => {
  if (keycloakInstance && keycloakInstance.authenticated) {
    try {
      await keycloakInstance.updateToken(30); // refresh if expiring within 30s
      config.headers.Authorization = `Bearer ${keycloakInstance.token}`;
    } catch (error) {
      console.error("Token refresh error in calendar-v2 interceptor:", error);
    }
  }
  return config;
});

/** Inclusive UTC bounds (ISO) spanning the user's local day for `day`. */
export const dayBoundsIso = (day: Date) => {
  const start = new Date(day);
  start.setHours(0, 0, 0, 0);
  const end = new Date(day);
  end.setHours(23, 59, 59, 999);
  return { occurs_from: start.toISOString(), occurs_to: end.toISOString() };
};

export const fetchCalendarV2EventsByDay = async (day: Date) => {
  const { occurs_from, occurs_to } = dayBoundsIso(day);

  const response = await client.get("/api/calendar/entries/search", {
    params: { occurs_from, occurs_to },
  });

  // Drop disabled entries and non-visible data categories (mirrors the Solid
  // client's fetch_events). Non-data classifications are always kept.
  return (response.data as CalendarV2Event[]).filter(isCalendarV2EventVisible);
};
